import { HttpClient } from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { PanelModule } from 'primeng/panel';
import { ToastModule } from 'primeng/toast';
import { InputTextModule } from 'primeng/inputtext';
import { DividerModule } from 'primeng/divider';
import { Router, NavigationEnd } from '@angular/router';
import { NgxMaskDirective, NgxMaskPipe } from 'ngx-mask';
import {
  PeopleService,
  RecipientCredentialsPaged,
} from '../../services/people.service';
import { CredentialRequest } from '../../types/credencial-request.type';
import { RecipientCredentials } from './../../types/recipient-credentials.response.type';
import { AddressService } from '../../services/address.service';
import { DialogService } from 'primeng/dynamicdialog';
import { AddressDialogComponent } from '../address-dialog/address-dialog.component';
import { Subscription, Subject, takeUntil } from 'rxjs';
import { AddressStatus } from '../../model/AddressStatus';
import { Documents } from '../../types/documents.response';
import { DocumentsDialogComponent } from '../documents/documents-dialog/documents-dialog.component';
import { DocumentType } from '../../types/document-type.enum';
import { YesornoDialogComponent } from '../yesorno-dialog/yesorno-dialog.component';
import { DatePipe } from '@angular/common';
import { DocumentService } from '../../services/document.service';
import { CpfSharedService } from 'src/app/services/cpf-shared.service';
import { NavigationStateService } from 'src/app/services/navigation-state.service';
import { CpfStateService, CpfInformState } from 'src/app/services/cpf-state.service';

interface CpfRecipientForm {
  cpf_recipient: FormControl<string | null>;
}

interface CpfApplicantForm {
  cpf_applicant: FormControl<string | null>;
}

@Component({
  selector: 'app-cpf-inform',
  standalone: true,
  providers: [MessageService],
  imports: [
    ReactiveFormsModule,
    NgxMaskDirective,
    NgxMaskPipe,
    FormsModule,
    DatePipe,
    ButtonModule,
    CardModule,
    PanelModule,
    ToastModule,
    InputTextModule,
    DividerModule,
  ],
  template: `
    <p-toast></p-toast>
    <div class="cpf-inform-container">
      <!-- Beneficiário -->
      <p-panel header="CPF do Beneficiário" [toggleable]="false" styleClass="compact-panel">
        <form [formGroup]="cpfRecipientForm" (submit)="onSubmitPerson('recipient')">
          <div class="form-container">
            <input
              #cpf_recipient
              id="cpf_recipient"
              pInputText
              aria-label="CPF"
              class="input-field"
              inputmode="numeric"
              mask="999.999.999-99"
              maxlength="14"
              formControlName="cpf_recipient"
              type="text"
              placeholder="000.000.000-00"
            />
            <p-button 
              label="Buscar" 
              icon="pi pi-search"
              type="submit"
              styleClass="search-button"
              size="small">
            </p-button>
          </div>
        </form>

        @if(recipient){
          <div class="person-data">
            <p-divider></p-divider>
            
            @if(recipientExpanded) {
              <h4 class="section-title">Dados do Beneficiário</h4>
              
              <div class="data-grid">
                <div class="data-row">
                  <span class="field-label">CPF/Nome:</span>
                  <span class="field-value uppercase">
                    {{ recipient.cpf | mask : '999.999.999-99' }} | {{ recipient.name }}
                  </span>
                </div>

                <div class="data-row">
                  <span class="field-label">Data de Nascimento:</span>
                  <span class="field-value">
                    {{ recipient.birthDate | date : 'dd/MM/yyyy' }}
                    <span class="age-info">{{ calculateAge(recipient.birthDate) }}</span>
                  </span>
                </div>

                <div class="data-row">
                  <span class="field-label">Endereço:</span>
                  <span class="field-value">
                    {{ recipient.address.street }}, {{ recipient.address.number }}
                    @if(recipient.address.compliment){ - {{ recipient.address.compliment }} }
                    @if(recipient.address.reference){ - {{ recipient.address.reference }} }
                    <br>
                    {{ recipient.address.neighborhood }} - {{ recipient.address.city }}/{{ recipient.address.state }}
                    <br>
                    CEP: {{ recipient.address.zipcode | mask : '00000-000' }}
                  </span>
                </div>

                <div class="data-row">
                  <span class="field-label">Contatos:</span>
                  <span class="field-value">
                    @if(recipient.email){ {{ recipient.email }} }
                    @if(recipient.homePhone){ | Tel: {{ recipient.homePhone | mask : '(00) 0000-0000' }} }
                    @if(recipient.mobilePhone){ | Cel: {{ recipient.mobilePhone | mask : '(00) 00000-0000' }} }
                  </span>
                </div>
              </div>

              <div class="button-row">
                @if(isRecipientAddressOutdated) {
                  <p-button
                    label="Atualizar Endereço"
                    icon="pi pi-map-marker"
                    (onClick)="onClickAddress(recipient.address.id, 'Beneficiário')"
                    severity="warning"
                    styleClass="action-button">
                  </p-button>
                } @else {
                  <p-button
                    label="Alterar Endereço"
                    icon="pi pi-map-marker"
                    (onClick)="onClickAddress(recipient.address.id, 'Beneficiário')"
                    styleClass="action-button">
                  </p-button>
                }
                
                @if(hasDocumentCreatedToday('recipient')){
                  <p-button
                    label="Alterar Documentos"
                    icon="pi pi-file"
                    (onClick)="onClickDocumentDialog(recipient.id, 'Beneficiário')"
                    styleClass="action-button">
                  </p-button>
                } @else {
                  <p-button
                    label="Incluir Documentos"
                    icon="pi pi-file"
                    (onClick)="onClickDocumentDialog(recipient.id, 'Beneficiário')"
                    severity="warning"
                    styleClass="action-button">
                  </p-button>
                }
                
                @if(valorToggle === 'false') {
                  <i 
                    class="pi pi-chevron-up collapse-icon" 
                    (click)="recipientExpanded = false"
                    title="Contrair">
                  </i>
                }
              </div>
            } @else {
              <!-- Versão contraída -->
              <div class="collapsed-view">
                <div class="data-row">
                  <span class="field-label">CPF/Nome:</span>
                  <span class="field-value uppercase">
                    {{ recipient.cpf | mask : '999.999.999-99' }} - {{ recipient.name }}
                  </span>
                  <i 
                    class="pi pi-chevron-down expand-icon" 
                    (click)="recipientExpanded = true"
                    title="Expandir">
                  </i>
                </div>
              </div>
            }
          </div>
        } @else {
          <p class="no-data-message">Dados do Beneficiário não disponíveis.</p>
        }
      </p-panel>

      <!-- Solicitante -->
      @if(valorToggle === 'false'){
        <p-panel header="CPF do Solicitante" [toggleable]="false" styleClass="compact-panel" [hidden]="isRecipient">
          <form [formGroup]="cpfApplicantForm" (submit)="onSubmitPerson('applicant')">
            <div class="form-container">
              <input
                #cpf_applicant
                id="cpf_applicant"
                pInputText
                aria-label="CPF"
                class="input-field"
                inputmode="numeric"
                mask="999.999.999-99"
                maxlength="14"
                formControlName="cpf_applicant"
                type="text"
                placeholder="000.000.000-00"
              />
              <p-button 
                label="Buscar" 
                icon="pi pi-search"
                type="submit"
                styleClass="search-button"
                size="small">
              </p-button>
            </div>
          </form>

          @if(applicant && applicantSearched){
            <div class="person-data">
              <p-divider></p-divider>
              <h4 class="section-title">Dados do Solicitante</h4>
              
              <div class="data-grid">
                <div class="data-row">
                  <span class="field-label">CPF/Nome:</span>
                  <span class="field-value uppercase">
                    {{ applicant.cpf | mask : '999.999.999-99' }} - {{ applicant.name }}
                  </span>
                </div>

                <div class="data-row">
                  <span class="field-label">Data de Nascimento:</span>
                  <span class="field-value">
                    {{ applicant.birthDate | date : 'dd/MM/yyyy' }}
                    <span class="age-info">{{ calculateAge(applicant.birthDate) }}</span>
                  </span>
                </div>

                <div class="data-row">
                  <span class="field-label">Endereço:</span>
                  <span class="field-value">
                    {{ applicant.address.street }}, {{ applicant.address.number }}
                    @if(applicant.address.compliment){ - {{ applicant.address.compliment }} }
                    @if(applicant.address.reference){ - {{ applicant.address.reference }} }
                    <br>
                    {{ applicant.address.neighborhood }} - {{ applicant.address.city }}/{{ applicant.address.state }}
                    <br>
                    CEP: {{ applicant.address.zipcode | mask : '00000-000' }}
                  </span>
                </div>

                <div class="data-row">
                  <span class="field-label">Contatos:</span>
                  <span class="field-value">
                    @if(applicant.email){ {{ applicant.email }} }
                    @if(applicant.homePhone){ | Tel: {{ applicant.homePhone | mask : '(00) 0000-0000' }} }
                    @if(applicant.mobilePhone){ | Cel: {{ applicant.mobilePhone | mask : '(00) 00000-0000' }} }
                  </span>
                </div>
              </div>

              <div class="button-row">
                @if(isApplicantAddressOutdated) {
                  <p-button
                    label="Atualizar Endereço"
                    icon="pi pi-map-marker"
                    (onClick)="onClickAddress(applicant.address.id, 'Solicitante')"
                    severity="warning"
                    styleClass="action-button">
                  </p-button>
                } @else {
                  <p-button
                    label="Alterar Endereço"
                    icon="pi pi-map-marker"
                    (onClick)="onClickAddress(applicant.address.id, 'Solicitante')"
                    styleClass="action-button">
                  </p-button>
                }
                
                @if(hasDocumentCreatedToday('applicant')){
                  <p-button
                    label="Alterar Documentos"
                    icon="pi pi-file"
                    (onClick)="onClickDocumentDialog(applicant.id, 'Solicitante')"
                    styleClass="action-button">
                  </p-button>
                } @else {
                  <p-button
                    label="Incluir Documentos (Opcional)"
                    icon="pi pi-file"
                    (onClick)="onClickDocumentDialog(applicant.id, 'Solicitante')"
                    styleClass="action-button">
                  </p-button>
                }
              </div>
            </div>
          } @else {
            <p class="no-data-message">Dados do Solicitante não disponíveis.</p>
          }
        </p-panel>
      }
    </div>
  `,
  styles: [
    `
      /* Container Principal */
      .cpf-inform-container {
        display: flex;
        flex-direction: column;
        gap: 1rem;
        width: 100%;
      }

      /* Panels Compactos */
      :host ::ng-deep .compact-panel {
        margin-bottom: 0;
      }

      :host ::ng-deep .compact-panel .p-panel-header {
        background: rgba(255, 230, 203, 0.1);
        border: 1px solid #FFE6CB;
        color: #325565;
        font-weight: 600;
        padding: 0.75rem 1rem;
        font-size: 0.9rem;
      }

      :host ::ng-deep .compact-panel .p-panel-content {
        padding: 1rem;
        border: 1px solid #FFE6CB;
        border-top: none;
      }

      /* Form Container */
      .form-container {
        display: flex;
        gap: 0.25rem;
        align-items: stretch;
        margin-bottom: 0.5rem;
      }

      /* Input Field */
      .input-field {
        width: 180px;
        height: 40px;
        padding: 0 12px;
        font-size: 0.875rem;
        border: 1px solid #d1d5db;
        border-radius: 4px;
        box-sizing: border-box;
        transition: border-color 0.2s;
      }

      .input-field:focus {
        outline: none;
        border-color: rgb(16, 148, 158);
        border-width: 2px;
      }

      /* Search Button */
      :host ::ng-deep .search-button .p-button {
        background: rgb(16, 148, 158) !important;
        border-color: rgb(16, 148, 158) !important;
        color: white !important;
        height: 40px;
        font-size: 0.875rem;
        padding: 0 1rem;
        white-space: nowrap;
        font-weight: 600;
      }

      :host ::ng-deep .search-button .p-button:hover {
        background: rgb(6, 182, 212) !important;
        border-color: rgb(6, 182, 212) !important;
      }

      /* Person Data Section */
      .person-data {
        margin-top: 1rem;
      }

      .section-title {
        color: #325565;
        font-weight: 600;
        font-size: 1rem;
        margin: 1rem 0 0.75rem 0;
      }

      /* Data Grid */
      .data-grid {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
      }

      .data-row {
        display: grid;
        grid-template-columns: 180px 1fr;
        gap: 0.5rem;
        padding: 0.5rem 0;
        border-bottom: 1px solid #f3f4f6;
      }

      .data-row:last-child {
        border-bottom: none;
      }

      .field-label {
        font-weight: 600;
        color: #325565;
        font-size: 0.875rem;
        line-height: 1.5;
      }

      .field-value {
        color: #6b7280;
        font-size: 0.875rem;
        line-height: 1.5;
      }

      .uppercase {
        text-transform: uppercase;
      }

      /* Age Info */
      .age-info {
        margin-left: 8px;
        color: #F15A22;
        font-weight: 600;
        font-size: 0.875rem;
      }

      /* Button Row */
      .button-row {
        display: flex;
        gap: 0.75rem;
        margin-top: 1rem;
        padding-top: 1rem;
        border-top: 1px solid #FFE6CB;
        flex-wrap: wrap;
      }

      /* Action Buttons */
      :host ::ng-deep .action-button .p-button {
        font-size: 0.875rem;
        padding: 0.5rem 1rem;
        height: auto;
      }

      :host ::ng-deep .p-button.p-button-warning {
        background: #F15A22 !important;
        border-color: #F15A22 !important;
      }

      :host ::ng-deep .p-button.p-button-warning:hover {
        background: #e54a1b !important;
        border-color: #e54a1b !important;
      }

      /* No Data Message */
      .no-data-message {
        color: #6b7280;
        font-size: 0.875rem;
        margin: 1rem 0;
        padding: 0.75rem;
        background: #f9fafb;
        border-radius: 4px;
        text-align: center;
      }

      /* Divider Customization */
      :host ::ng-deep .p-divider {
        margin: 1rem 0;
      }

      /* Responsive Design */
      @media (max-width: 768px) {
        .data-row {
          grid-template-columns: 1fr;
          gap: 0.25rem;
        }

        .button-row {
          flex-direction: column;
        }

        :host ::ng-deep .action-button {
          width: 100%;
        }

        .form-container {
          flex-direction: column;
        }

        .input-field {
          width: 100%;
        }
      }

      /* Toast Customization */
      :host ::ng-deep .p-toast {
        z-index: 9999;
      }

      /* Collapsed View */
      .collapsed-view {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
      }

      .collapsed-view .data-row {
        display: grid;
        grid-template-columns: 180px 1fr auto;
        align-items: center;
      }

      /* Toggle Icons */
      .collapse-icon,
      .expand-icon {
        font-size: 1.25rem;
        color: #6b7280;
        cursor: pointer;
        transition: all 0.2s ease;
        padding: 0.25rem;
        border-radius: 4px;
      }

      .collapse-icon:hover,
      .expand-icon:hover {
        color: #325565;
        background: rgba(107, 114, 128, 0.1);
      }

      .collapse-icon {
        margin-left: auto;
      }

      .expand-icon {
        justify-self: end;
      }
    `,
  ],
})
export class CpfInformComponent implements OnInit, OnChanges, OnDestroy {
  private destroyed$ = new Subject<void>();
  private createdSubscription: Subscription = new Subscription();
  private lastSearchedCpf: string = '';

  isRecipient: boolean = false;
  applicantSearched: boolean = false;
  recipientExpanded: boolean = true;

  cpfRecipientForm!: FormGroup<CpfRecipientForm>;
  cpfApplicantForm: FormGroup<CpfApplicantForm>;
  people: any = null;
  recipient: any = null;
  applicant: any = null;
  recipientlist: RecipientCredentials[] = [];
  recipientlistPaged: RecipientCredentialsPaged[] = [];
  documentList: Documents[] = [];
  documentType: DocumentType[] = [];

  countApplicantDocuments: number = 0;
  countRecipientDocuments: number = 0;

  recipientId: number = 0;
  applicantId: number = 0;
  observation: string = '';

  recipientName: string = '';
  applicantName: string = '';

  isLaudoMedicoRecipient: boolean = false;
  isLaudoMedicoApplicant: boolean = false;
  isComprovanteAddressTodayRecipient: boolean = false;
  isComprovanteAddressTodayApplicant: boolean = false;

  private isProcessingCadastrado = false;
  private isDialogOpen = false;

  private currentPerson: 'recipient' | 'applicant' = 'recipient';

  private _formBuilder = inject(FormBuilder);

  @ViewChild('cpf_recipient') recipientInputField!: ElementRef;
  @ViewChild('cpf_applicant') applicantInputField!: ElementRef;

  @Output() emissorRecipientList = new EventEmitter<RecipientCredentials[]>();
  @Output() emissorCredencialsEnvolveds = new EventEmitter<CredentialRequest>();
  @Output() emissorAddressStatus = new EventEmitter<AddressStatus>();

  @Input() valorToggle: string | undefined;

  private applicantSubscription: Subscription | null = null;
  private recipientSubscription: Subscription | null = null;

  requestBody: CredentialRequest = {
    recipientName: '',
    applicantName: '',
    recipientId: 0,
    applicantId: 0,
    processNumber: '',
    observation: '',
    isTea: false,
    prompt: '',
  };

  isRecipientAddressOutdated: boolean = false;
  isApplicantAddressOutdated: boolean = false;
  isRecipientDocumentsOutdated: boolean = false;
  isApplicantDocumentsOutdated: boolean = false;

  addressStatus: AddressStatus = {
    isRecipientAddressOutdated: false,
    isApplicantAddressOutdated: false,
    isLaudoMedicoRecipient: false,
    isComprovanteAddressTodayRecipient: false,
    isLaudoMedicoApplicant: false,
    isComprovanteAddressTodayApplicant: false,
  };

  constructor(
    private cpfSharedService: CpfSharedService,
    private cpfStateService: CpfStateService,
    private navigationState: NavigationStateService,
    private peopleService: PeopleService,
    private addressService: AddressService,
    private documentService: DocumentService,
    private router: Router,
    private messageService: MessageService,
    private dialogService: DialogService,
    private http: HttpClient,
    private cdr: ChangeDetectorRef
  ) {
    this.cpfRecipientForm = new FormGroup({
      cpf_recipient: new FormControl('', [
        Validators.required,
        Validators.pattern(/^(\d{3}\.\d{3}\.\d{3}-\d{2}|\d{11})$/),
      ]),
    });
    this.cpfApplicantForm = new FormGroup({
      cpf_applicant: new FormControl('', [
        Validators.pattern(/^(\d{3}\.\d{3}\.\d{3}-\d{2}|\d{11})$/),
      ]),
    });
  }

  ngOnInit() {
    // Carregar estado salvo ao inicializar
    this.loadSavedState();
    
    this.currentPerson = 'recipient';

    // Forçar detecção de mudanças
    this.cdr.detectChanges();
    
    setTimeout(() => {
      this.focusOnCurrentPersonField();
    }, 0);

    this.navigationState.currentPerson$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(person => {
        this.currentPerson = person;
        this.loadStoredCpfForCurrentPerson();
      });

    this.setupCpfSubscriptions();
    this.setupCpfCadastradoSubscription();
    this.setupNavigationListener();
  }

  ngOnDestroy(): void {
    // Salvar estado antes de destruir o componente
    this.saveCurrentState();
    
    this.destroyed$.next();
    this.destroyed$.complete();
    
    if (this.applicantSubscription) {
      this.applicantSubscription.unsubscribe();
    }
    if (this.recipientSubscription) {
      this.recipientSubscription.unsubscribe();
    }
    if (this.createdSubscription) {
      this.createdSubscription.unsubscribe();
    }
    
    // console.log('CpfInformComponent destruído');
  }

  private setupCpfSubscriptions(): void {
    this.cpfSharedService.lastRecipientCpf$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(cpf => {
        if (cpf && this.currentPerson === 'recipient') {
          const formattedCpf = this.formatCpfForDisplay(cpf);
          this.cpfRecipientForm.patchValue({ cpf_recipient: formattedCpf });
          this.cdr.detectChanges();
          
          // Se não há recipient carregado ou o CPF é diferente, dispara a busca automaticamente
          const cleanCpf = cpf.replace(/\D/g, '');
          if (!this.recipient || this.recipient.cpf !== cleanCpf) {
            setTimeout(() => {
              this.executePersonSearch('recipient', cleanCpf);
            }, 100);
          }
        }
      });

    this.cpfSharedService.lastApplicantCpf$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(cpf => {
        if (cpf && this.currentPerson === 'applicant') {
          const formattedCpf = this.formatCpfForDisplay(cpf);
          this.cpfApplicantForm.patchValue({ cpf_applicant: formattedCpf });
          this.cdr.detectChanges();
        }
      });
  }

  private setupCpfCadastradoSubscription(): void {
    this.cpfSharedService.cpfCadastrado$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(cpfCadastrado => {
        if (cpfCadastrado && !this.isProcessingCadastrado) {
          // console.log('CPF cadastrado detectado no CpfInformComponent:', cpfCadastrado);
          
          this.isProcessingCadastrado = true;
          
          try {
            // Determinar para qual pessoa o CPF foi cadastrado
            const currentPerson = this.cpfSharedService.getCurrentPerson();
            const recipientCpf = this.cpfSharedService.getRecipientCpf();
            const applicantCpf = this.cpfSharedService.getApplicantCpf();
            
            // console.log('Current person:', currentPerson);
            // console.log('Recipient CPF:', recipientCpf);
            // console.log('Applicant CPF:', applicantCpf);
            
            // Verificar para qual pessoa o CPF foi cadastrado
            if (currentPerson === 'recipient' || recipientCpf === cpfCadastrado) {
              // console.log('Buscando como recipient...');
              this.buscarCPFAposCadastro(cpfCadastrado);
            } else if (currentPerson === 'applicant' || applicantCpf === cpfCadastrado) {
              // console.log('Buscando como applicant...');
              this.buscarApplicantAposCadastro(cpfCadastrado);
            } else {
              // Se não conseguiu determinar, tenta como recipient (comportamento padrão)
              // console.log('Não foi possível determinar a pessoa, buscando como recipient...');
              this.buscarCPFAposCadastro(cpfCadastrado);
            }
          } finally {
            setTimeout(() => {
              this.cpfSharedService.setCpfCadastrado('');
              this.isProcessingCadastrado = false;
            }, 500);
          }
        }
      });
  }

  private setupNavigationListener(): void {
    this.router.events
      .pipe(takeUntil(this.destroyed$))
      .subscribe(event => {
        if (event instanceof NavigationEnd) {
          this.saveCurrentState();
        }
      });
  }

  private loadSavedState(): void {
    const savedState = this.cpfStateService.getState();
    if (savedState) {
      // console.log('Carregando estado salvo do CpfInformComponent:', savedState);
      
      // Restaurar dados do recipient
      if (savedState.recipient) {
        this.recipient = savedState.recipient;
        this.recipientId = savedState.recipientId;
        this.recipientName = savedState.recipientName;
        this.recipientlist = savedState.recipientlist || [];
        this.documentList = savedState.documentList || [];
        this.countRecipientDocuments = savedState.countRecipientDocuments || 0;
      }

      // Restaurar dados do applicant
      if (savedState.applicant) {
        this.applicant = savedState.applicant;
        this.applicantId = savedState.applicantId;
        this.applicantName = savedState.applicantName;
        this.countApplicantDocuments = savedState.countApplicantDocuments || 0;
      }

      // Restaurar CPFs nos formulários
      if (savedState.recipientCpf) {
        const formattedCpf = this.formatCpfForDisplay(savedState.recipientCpf);
        this.cpfRecipientForm.patchValue({ cpf_recipient: formattedCpf });
      }
      
      if (savedState.applicantCpf) {
        const formattedCpf = this.formatCpfForDisplay(savedState.applicantCpf);
        this.cpfApplicantForm.patchValue({ cpf_applicant: formattedCpf });
      }

      // Restaurar pessoa atual
      if (savedState.currentPerson) {
        this.currentPerson = savedState.currentPerson;
        this.navigationState.setCurrentPerson(savedState.currentPerson);
      }

      // Restaurar status do endereço
      this.isRecipientAddressOutdated = savedState.isRecipientAddressOutdated || false;
      this.isApplicantAddressOutdated = savedState.isApplicantAddressOutdated || false;

      // Emitir eventos para atualizar componentes pais
      this.emitCredentialEnvolveds();
      this.emitAddressStatus();
      
      this.cdr.detectChanges();
    } else {
      // Se não há estado salvo, tentar carregar apenas os CPFs persistidos
      this.loadPersistedCpfs();
    }
  }

  private loadPersistedCpfs(): void {
    const persistedCpfs = this.cpfSharedService.getBothCpfs();
    
    if (persistedCpfs.recipientCpf) {
      const formattedCpf = this.formatCpfForDisplay(persistedCpfs.recipientCpf);
      this.cpfRecipientForm.patchValue({ cpf_recipient: formattedCpf });
    }
    
    if (persistedCpfs.applicantCpf) {
      const formattedCpf = this.formatCpfForDisplay(persistedCpfs.applicantCpf);
      this.cpfApplicantForm.patchValue({ cpf_applicant: formattedCpf });
    }
  }

  private saveCurrentState(): void {
    const state: CpfInformState = {
      recipient: this.recipient,
      applicant: this.applicant,
      recipientId: this.recipientId,
      applicantId: this.applicantId,
      recipientName: this.recipientName,
      applicantName: this.applicantName,
      recipientlist: this.recipientlist,
      documentList: this.documentList,
      countRecipientDocuments: this.countRecipientDocuments,
      countApplicantDocuments: this.countApplicantDocuments,
      isRecipientAddressOutdated: this.isRecipientAddressOutdated,
      isApplicantAddressOutdated: this.isApplicantAddressOutdated,
      recipientCpf: this.cpfSharedService.getRecipientCpf(),
      applicantCpf: this.cpfSharedService.getApplicantCpf(),
      currentPerson: this.currentPerson,
      timestamp: new Date().getTime()
    };
    
    this.cpfStateService.setState(state);
    // console.log('Estado do CpfInformComponent salvo');
  }

  private loadStoredCpfForCurrentPerson(): void {
    if (!this.currentPerson) return;
    
    const storedCpf = this.currentPerson === 'recipient' 
      ? this.cpfSharedService.getRecipientCpf()
      : this.cpfSharedService.getApplicantCpf();

    if (storedCpf) {
      this.handleIncomingCpf(storedCpf, this.currentPerson);
    }
  }

  private handleIncomingCpf(cpf: string, targetPerson: 'recipient' | 'applicant'): void {
    if (!cpf) return;
    
    const formattedCpf = this.formatCpfForDisplay(cpf);
    
    try {
      if (targetPerson === 'recipient') {
        this.cpfRecipientForm.patchValue({ cpf_recipient: formattedCpf });
      } else {
        this.cpfApplicantForm.patchValue({ cpf_applicant: formattedCpf });
      }
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Erro ao processar CPF:', error);
    }
  }

  onSubmitPerson(person: 'recipient' | 'applicant') {
    if (this.isProcessingCadastrado) {
      return;
    }

    this.currentPerson = person;
    this.navigationState.setCurrentPerson(person);
    
    let cpfValue: string;
    if (person === 'recipient') {
      // Limpar dados do applicant ao buscar novo recipient
      this.applicant = null;
      this.applicantId = 0;
      this.applicantName = '';
      this.countApplicantDocuments = 0;
      this.applicantSearched = false;
      
      // Atualizar o toggle para true quando buscar recipient
      this.cpfSharedService.setToggleValue(true);
      
      cpfValue = this.cpfRecipientForm.get('cpf_recipient')?.value || '';
      
      // Limpar dados do recipient se estiver buscando um NOVO recipient
      if (cpfValue && this.recipient && this.cleanCpf(cpfValue) !== this.recipient.cpf) {
        this.recipient = null;
        this.recipientlist = [];
        this.documentList = [];
        this.recipientId = 0;
        this.recipientName = '';
        this.countRecipientDocuments = 0;
      }
    } else {
      cpfValue = this.cpfApplicantForm.get('cpf_applicant')?.value || '';
      
      // Limpar dados do applicant se estiver buscando um NOVO applicant
      if (cpfValue && this.applicant && this.cleanCpf(cpfValue) !== this.applicant.cpf) {
        this.applicant = null;
        this.applicantId = 0;
        this.applicantName = '';
        this.countApplicantDocuments = 0;
        this.applicantSearched = false;
      }
      
      // Marcar que o applicant foi buscado explicitamente
      this.applicantSearched = true;
    }

    this.cdr.detectChanges();

    if (this.valorToggle === 'true' && person === 'recipient') {
      this.applicantId = 0;
      this.applicantName = '';
    }

    if (!cpfValue) {
      this.showSnackBar('CPF inválido ou não informado.');
      return;
    }

    const cleanCpf = this.cleanCpf(cpfValue);

    if (cleanCpf.length !== 11) {
      this.showSnackBar('CPF inválido. Deve conter 11 dígitos.');
      return;
    }

    if (person === 'recipient') {
      this.cpfSharedService.setRecipientCpf(cleanCpf);
      this.lastSearchedCpf = cleanCpf;
    } else {
      this.cpfSharedService.setApplicantCpf(cleanCpf);
    }

    this.executePersonSearch(person, cleanCpf);
  }

  private executePersonSearch(person: 'recipient' | 'applicant', cleanCpf: string): void {
    if (person === 'applicant' && this.applicantSubscription) {
      this.applicantSubscription.unsubscribe();
    }
    if (person === 'recipient' && this.recipientSubscription) {
      this.recipientSubscription.unsubscribe();
      this.isRecipient = true;
    }

    const subscription = this.peopleService.getPeopleByCpf(cleanCpf)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response) => {
          if (response.data && response.data.length > 0) {
            this.handlePersonFound(response.data[0], person, cleanCpf);
          } else {
            this.handlePersonNotFound(cleanCpf, person);
          }
        },
        error: (error) => {
          this.handleSearchError(error, cleanCpf, person);
        }
      });

    if (person === 'applicant') {
      this.applicantSubscription = subscription;
    } else {
      this.recipientSubscription = subscription;
    }
  }

  private handlePersonFound(personData: any, person: 'recipient' | 'applicant', searchedCpf: string): void {
    if (person === 'recipient') {
      this.cpfSharedService.clearRecipientCpf();
    } else {
      this.cpfSharedService.clearApplicantCpf();
    }
    
    this.cpfSharedService.clearCpf();

    if (person === 'recipient') {
      this.recipient = personData;
      this.recipientlist = this.recipient.recipientCredentials || [];
      this.documentList = this.recipient.documentList || [];
      this.recipientId = this.recipient.id;
      this.recipientName = this.recipient.name;

      // Quando buscar recipient, definir applicant como o mesmo (toggle true)
      if (!this.applicantId) {
        this.applicantId = this.recipientId;
        this.applicantName = this.recipientName;
        this.applicant = this.recipient;
      }

      this.emissorRecipientList.emit(this.recipientlist);
      this.countRecipientDocuments = this.recipient.documentList?.length || 0;
      
    } else if (person === 'applicant') {
      this.applicant = personData;
      this.applicantId = this.applicant.id;
      this.applicantName = this.applicant.name;
      this.countApplicantDocuments = this.applicant.documentList?.length || 0;
    }

    this.checkAddressStatus(person);
    this.emitAddressStatus();
    this.emitCredentialEnvolveds();

    // Salvar estado após encontrar pessoa
    this.saveCurrentState();

    this.focusOnNextField(person);
  }

  private buscarCPFAposCadastro(cpf: string) {
    const cpfFormatado = this.formatCpfForDisplay(cpf);
    
    // console.log('Buscando CPF após cadastro:', cpf);
    
    // Limpar dados anteriores do applicant quando buscar novo recipient
    this.applicant = null;
    this.applicantId = 0;
    this.applicantName = '';
    this.countApplicantDocuments = 0;
    
    // Atualizar toggle para true
    this.cpfSharedService.setToggleValue(true);
    
    // Preencher o formulário com o novo CPF
    this.cpfRecipientForm.patchValue({
      cpf_recipient: cpfFormatado
    });

    this.cpfSharedService.setRecipientCpf(cpf);

    setTimeout(() => {
      // console.log('Executando busca automática após cadastro...');
      this.executeDirectSearch('recipient', this.cleanCpf(cpf));
    }, 800);
  }

  private buscarApplicantAposCadastro(cpf: string) {
    const cpfFormatado = this.formatCpfForDisplay(cpf);
    
    // console.log('Buscando applicant CPF após cadastro:', cpf);
    
    // Preencher o formulário do applicant
    this.cpfApplicantForm.patchValue({
      cpf_applicant: cpfFormatado
    });

    this.cpfSharedService.setApplicantCpf(cpf);

    setTimeout(() => {
      // console.log('Executando busca automática do applicant após cadastro...');
      this.executeDirectSearch('applicant', this.cleanCpf(cpf));
    }, 800);
  }

  private executeDirectSearch(person: 'recipient' | 'applicant', cleanCpf: string): void {
    if (person === 'applicant' && this.applicantSubscription) {
      this.applicantSubscription.unsubscribe();
    }
    if (person === 'recipient' && this.recipientSubscription) {
      this.recipientSubscription.unsubscribe();
    }

    const subscription = this.peopleService.getPeopleByCpf(cleanCpf)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response) => {
          if (response.data && response.data.length > 0) {
            this.handlePersonFound(response.data[0], person, cleanCpf);
          } else {
            // console.log('CPF ainda não encontrado após cadastro, aguardando...');
          }
        },
        error: (error) => {
          console.error('Erro na busca direta após cadastro:', error);
        }
      });

    if (person === 'applicant') {
      this.applicantSubscription = subscription;
    } else {
      this.recipientSubscription = subscription;
    }
  }

  private handlePersonNotFound(cleanCpf: string, person: 'recipient' | 'applicant'): void {
    if (this.isDialogOpen) {
      return;
    }

    this.isDialogOpen = true;

    // IMPORTANTE: Salvar o estado atual ANTES de abrir o dialog de cadastro
    this.saveCurrentState();

    if (person === 'recipient') {
      this.cpfSharedService.setRecipientCpf(cleanCpf);
    } else {
      this.cpfSharedService.setApplicantCpf(cleanCpf);
    }

    this.cpfSharedService.setCpf(cleanCpf);

    const type = 'cpf';
    const value = 0;
    const message = 'O CPF informado não está cadastrado! Deseja cadastrar?';
    
    const dialogRef = this.dialogService.open(YesornoDialogComponent, {
      header: 'Confirmação',
      width: '350px',
      data: { type, value, message },
    });

    if (dialogRef) {
      dialogRef.onClose
        .pipe(takeUntil(this.destroyed$))
        .subscribe((result: any) => {
          this.isDialogOpen = false;

          if (result?.confirmed) {
          // console.log(`Cadastro confirmado para CPF: ${cleanCpf}`);
          // O estado já foi salvo antes de abrir o dialog
        } else {
          if (person === 'recipient') {
            this.cpfSharedService.clearRecipientCpf();
            this.cpfRecipientForm.patchValue({ cpf_recipient: '' });
          } else {
            this.cpfSharedService.clearApplicantCpf();
            this.cpfApplicantForm.patchValue({ cpf_applicant: '' });
          }
          this.cpfSharedService.clearCpf();
          
          // Restaurar o estado se o usuário cancelou o cadastro
          this.loadSavedState();
        }
      });
    }
  }

  private handleSearchError(error: any, cleanCpf: string, person: 'recipient' | 'applicant'): void {
    if (error.status === 404) {
      this.handlePersonNotFound(cleanCpf, person);
    } else {
      console.error(`Erro ao buscar CPF do ${person}:`, error);
      this.showSnackBar(
        `Erro ao buscar CPF do ${person === 'recipient' ? 'beneficiário' : 'solicitante'}. Tente novamente.`
      );
      
      if (person === 'recipient') {
        this.cpfSharedService.clearRecipientCpf();
      } else {
        this.cpfSharedService.clearApplicantCpf();
      }
      this.cpfSharedService.clearCpf();
    }
  }

  private showSnackBar(message: string): void {
    this.messageService.add({
      severity: 'info',
      summary: 'Informação',
      detail: message,
      life: 3000
    });
  }

  private formatCpfForDisplay(cpf: string): string {
    const cleanCpf = cpf.replace(/\D/g, '');
    if (cleanCpf.length === 11) {
      return cleanCpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }
    return cpf;
  }

  private focusOnCurrentPersonField(): void {
    setTimeout(() => {
      try {
        if (this.currentPerson === 'recipient') {
          this.recipientInputField?.nativeElement?.focus();
        } else if (this.currentPerson === 'applicant') {
          this.applicantInputField?.nativeElement?.focus();
        }
      } catch (error) {
        console.warn('Não foi possível focar no campo:', error);
      }
    }, 0);
  }

  private focusOnNextField(currentPerson: 'recipient' | 'applicant'): void {
    setTimeout(() => {
      if (currentPerson === 'recipient' && this.valorToggle === 'false') {
        this.applicantInputField?.nativeElement?.focus();
      } else if (currentPerson === 'applicant') {
        this.recipientInputField?.nativeElement?.focus();
      }
    }, 100);
  }

  private cleanCpf(cpf: string): string {
    return cpf.replace(/\D/g, '');
  }

  ngOnChanges(changes: SimpleChanges) {
    this.cdr.detectChanges();

    if (changes['valorToggle']) {
      const currentValue = changes['valorToggle'].currentValue;
      
      if (currentValue === 'false') {
        this.isRecipient = false;
        this.destroyApplicantSubscription();
        this.currentPerson = 'applicant';
        this.navigationState.setCurrentPerson('applicant');
        
        // Contrair o painel do beneficiário quando solicitante for diferente
        this.recipientExpanded = false;
        
        setTimeout(() => {
          this.applicantInputField?.nativeElement?.focus();
        }, 0);
        this.emitCredentialEnvolveds();
      } else {
        this.destroyApplicantSubscription();
        this.applicantId = this.recipientId;
        this.applicantName = this.recipientName;
        this.currentPerson = 'recipient';
        this.navigationState.setCurrentPerson('recipient');
        
        // Expandir o painel do beneficiário quando solicitante for o mesmo
        this.recipientExpanded = true;
        
        if (this.recipientName) {
          this.emitCredentialEnvolveds();
        }
      }

      // Salvar estado quando o toggle mudar
      this.saveCurrentState();
    }
  }

  emitCredentialEnvolveds() {
    if (this.requestBody && this.recipient) {
      if (!this.applicantId) {
        this.requestBody.applicantId = this.recipient.id;
        this.requestBody.applicantName = this.recipient.name;
      } else {
        this.requestBody.applicantId = this.applicantId;
        this.requestBody.applicantName = this.applicantName;
      }
      this.requestBody.recipientId = this.recipient.id;
      this.requestBody.recipientName = this.recipient.name;
      this.requestBody.observation = '';
      this.emissorCredencialsEnvolveds.emit(this.requestBody);

      this.clearRequestBody();
    } else {
      console.error('Credential or name is null:', this.requestBody);
    }
  }

  private clearRequestBody(): void {
    this.requestBody = {
      recipientName: '',
      applicantName: '',
      recipientId: 0,
      applicantId: 0,
      processNumber: '',
      observation: '',
      isTea: false,
      prompt: '',
    };
  }

  onClickAddress(addressId: number, person: string) {
    this.addressService.getAddressById(addressId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response) => {
          if (response) {
            const dialogRef = this.dialogService.open(AddressDialogComponent, {
              header: 'Atualizar Endereço do ' + person,
              width: '900px',
              data: { address: response, person },
            });

            if (dialogRef) {
              dialogRef.onClose
                .pipe(takeUntil(this.destroyed$))
                .subscribe((result) => {
                  if (result?.saved) {
                  if (result.person === 'Beneficiário') {
                    this.onSubmitPerson('recipient');
                  } else if (result.person === 'Solicitante') {
                    this.onSubmitPerson('applicant');
                  }
                  
                  // Salvar estado após atualizar endereço
                  this.saveCurrentState();
                }
              });
            }
          } else {
            this.showSnackBar('Nenhum dado encontrado.');
          }
        },
        error: (error) => {
          this.showSnackBar('Erro ao buscar Address. Tente novamente.');
        },
      });
  }

  checkAddressStatus(person: string) {
    const currentDate = new Date();

    const formatDate = (date: Date): string => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const currentFormatted = formatDate(currentDate);

    if (person === 'recipient' && this.recipient && this.recipient.address) {
      const recipientUpdatedAtFormatted = formatDate(
        new Date(this.recipient.address.updatedAt)
      );

      this.isRecipientAddressOutdated = recipientUpdatedAtFormatted < currentFormatted;
    } else if (person === 'applicant' && this.applicant && this.applicant.address) {
      const applicantUpdatedAtFormatted = formatDate(
        new Date(this.applicant.address.updatedAt)
      );
      this.isApplicantAddressOutdated = applicantUpdatedAtFormatted < currentFormatted;
    }
  }

  emitAddressStatus() {
    this.addressStatus.isRecipientAddressOutdated = this.isRecipientAddressOutdated;
    this.addressStatus.isApplicantAddressOutdated = this.isApplicantAddressOutdated;
    this.addressStatus.isLaudoMedicoRecipient = this.isLaudoMedicoRecipient;
    this.addressStatus.isComprovanteAddressTodayRecipient = this.isComprovanteAddressTodayRecipient;
    this.addressStatus.isLaudoMedicoApplicant = this.isLaudoMedicoApplicant;
    this.addressStatus.isComprovanteAddressTodayApplicant = this.isComprovanteAddressTodayApplicant;
    
    this.emissorAddressStatus.emit(this.addressStatus);
  }

  hasDocumentCreatedToday(person: string): boolean {
    const today = new Date();
    const todayString = today.toISOString().split('T')[0];
    
    let documents: Documents[] = [];
    
    if (person === 'recipient' && this.recipient) {
      documents = this.recipient.documentList || [];
    } else if (person === 'applicant' && this.applicant) {
      documents = this.applicant.documentList || [];
    }
    
    return documents.some(document => {
      const documentDate = document.createdAt?.split('T')[0];
      return documentDate === todayString;
    });
  }

  loadDocuments(recipientId: number) {
    this.documentService.getAllDocumentsByPeopleId(recipientId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (data) => {
          this.documentList = data;
          this.cdr.detectChanges();
          
          // Salvar estado após carregar documentos
          this.saveCurrentState();
        },
        error: (error) => {
          console.error('Erro ao carregar documentos:', error);
          this.showSnackBar('Erro ao carregar documentos. Tente novamente.');
        },
      });
  }

  destroyApplicantSubscription() {
    if (this.applicantSubscription) {
      this.applicantSubscription.unsubscribe();
      this.applicantSubscription = null;
      this.applicant = null;
      this.cdr.detectChanges();
      this.emitCredentialEnvolveds();
      
      // Salvar estado após destruir subscription
      this.saveCurrentState();
    }
  }

  onClickDocumentDialog(peopleId: number, person: string) {
    this.peopleService.getPeopleById(peopleId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response) => {
          const peopleData = response;
          const personName = peopleData.data.name;
          const dialogRef = this.dialogService.open(DocumentsDialogComponent, {
            width: '90vw',
            height: '87vh',
            data: {
              documents: this.documentList,
              person,
              personName,
              peopleId,
            },
          });

          if (dialogRef) {
            dialogRef.onClose
              .pipe(takeUntil(this.destroyed$))
              .subscribe((result: any) => {
                if (result?.saved) {
                if (result.person === 'Beneficiário') {
                  this.onSubmitPerson('recipient');
                } else if (result.person === 'Solicitante') {
                  this.onSubmitPerson('applicant');
                }
                
                // Salvar estado após fechar dialog de documentos
                this.saveCurrentState();
              }
            });
          }
        },
        error: (error) => {
          this.showSnackBar('Erro ao buscar Documentos. Tente novamente.');
        },
      });
  }

  // Método para calcular a idade com base na data de nascimento
  calculateAge(birthDate: string): string {
    if (!birthDate) {
      return '';
    }
    
    const birth = new Date(birthDate);
    const today = new Date();
    
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    
    return age > 0 ? `(${age} anos)` : '';
  }

  // Método público para limpar estado (opcional)
  clearState(): void {
    this.cpfStateService.clearState();
    this.cpfSharedService.clearBothCpfs();
  }
}